import React, { useRef, useState, useEffect, forwardRef } from 'react';

/*------------- IMPORTS FROM MATERIAL UI ------------------------------------*/
import Form from 'rsuite/Form';
import Button from 'rsuite/Button';
import FlexboxGrid from 'rsuite/FlexboxGrid';
import Grid from 'rsuite/Grid';
import Row from 'rsuite/Row';
import Col from 'rsuite/Col';
import { Notification, useToaster } from 'rsuite';
import SelectPicker from 'rsuite/SelectPicker';

/*------------- OTHERS ------------------------------------------------------*/
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { SchemaModel, StringType, NumberType } from 'schema-typed';

/*------------- CUSTOM ------------------------------------------------------*/
import 'Components/Admin/User/table.css';
import { SaveUserAsync, RequestUsersAsync, UpdateUserAsync } from 'slices/userSlice';

const roles = [
  {
    value: 100,
    label: 'Administrador',
  },
  {
    value: 101,
    label: 'Colaborador',
  },
];

const model = SchemaModel({
  nombre: StringType().isRequired('Campo requerido'),
  apPaterno: StringType(),
  apMaterno: StringType(),
  email: StringType()
    .isEmail('Por favor ingrese la dirección de correo electrónico correcta')
    // .addRule(async (value, data) => {
    //   let res = await asyncCheckUsername(value);
    //   return res;
    // }, 'Correo registrado')
    .isRequired('Este campo no puede estar vacío'),
  password: StringType().isRequired('Campo requerido'),
  rol: NumberType().isRequired('Campo requerido'),
});

const TextField = forwardRef((props, ref) => {
  const { name, label, accepter, ...rest } = props;

  return (
    <Form.Group controlId={`${name}-4`} ref={ref}>
      <Form.ControlLabel>{label} </Form.ControlLabel>
      <Form.Control name={name} accepter={accepter} {...rest} />
    </Form.Group>
  );
});

export default function UserForm(props) {
  const formRef = useRef();
  const dispatch = useDispatch();
  const toaster = useToaster();
  const [loading, setLoading] = useState(false);
  const { data, close } = props;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [, setFormError] = useState({});
  const [formValue, setFormValue] = useState({
    id: null,
    nombre: '',
    apPaterno: '',
    apMaterno: '',
    email: '',
    password: '',
    rol: '',
  });

  const handleSubmit = async () => {
    let formStatus = await formRef.current.checkAsync();
    if (formStatus.hasError) {
      return;
    } else {
      if (formValue && formValue.id) {
        setLoading(true);
        dispatch(UpdateUserAsync(formValue))
          .then((res) => {
            toaster.push(
              <Notification type={'success'} header={`DTE Manager`} closable>
                <p>Usuario guardado con exito</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
            dispatch(RequestUsersAsync({
              search: '',
              numberPage: 1,
              sizePage: 11,
            }));
            close();
          })
          .catch((err) => {            
            let message = err.response.data;
            toaster.push(
              <Notification type={'error'} header={`DTE Manager`} closable>
                <p>{message}</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
          });
      } else {
        setLoading(true);
        dispatch(SaveUserAsync(formValue))
          .then((res) => {
            toaster.push(
              <Notification type={'success'} header={`DTE Manager`} closable>
                <p>Usuario creado con exito</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
            dispatch(RequestUsersAsync({}));
            close();
          })
          .catch((err) => {
            let message = err.response.data;
            toaster.push(
              <Notification type={'error'} header={`DTE Manager`} closable>
                <p>{message}</p>
              </Notification>,
              {placement: 'topEnd'}
            );
            setLoading(false);
          });
      }
    }
  };

  const handleChangeForm = (value) => {
    setFormValue(value);
  };

  useEffect(() => {
    if (data) {
      setFormValue((prevState) => ({
        ...prevState,
        id: data.id,
        nombre: data.nombre,
        apPaterno: data.apPaterno,
        apMaterno: data.apMaterno,
        email: data.email,
        rol: data.rol,
        password: data.password,
      }));
    }
  }, [data]);

  return (
    <Grid fluid>
      <Row>
        <Col xs={24}>
          <Form ref={formRef} onChange={handleChangeForm} onCheck={setFormError} formValue={formValue} model={model}>
            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={24}>
                  <TextField name="nombre" placeholder="Nombre" classPrefix="full_width" label="Nombre" autoComplete="off" />
                </Col>
              </Row>
            </Grid>
            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={12}>
                  <TextField
                    name="apPaterno"
                    placeholder="Apellido Paterno"
                    classPrefix="full_width"
                    label="Apellido Paterno"
                    autoComplete="off"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <TextField
                    name="apMaterno"
                    placeholder="Apellido Materno"
                    classPrefix="full_width"
                    label="Apellido Materno"
                    autoComplete="off"
                  />
                </Col>
              </Row>
            </Grid>
            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={12}>
                  <TextField
                    checkAsync
                    name="email"
                    classPrefix="full_width"
                    placeholder="Correo electrónico"
                    label="Correo electrónico"
                    autoComplete="new-email"
                  />
                </Col>
                <Col xs={24} md={12}>
                  <TextField
                    name="password"
                    placeholder="Contraseña"
                    classPrefix="full_width"
                    label="Contraseña"
                    type="password"
                    autoComplete="new-password"
                  />
                </Col>
              </Row>
            </Grid>
            <Grid fluid>
              <Row gutter={16} className="show-grid">
                <Col xs={24} md={12}>
                  <TextField name="rol" classPrefix="full_width" block accepter={SelectPicker} data={roles} placeholder="Rol" label="Rol" />
                </Col>
                <Col xs={24} md={12}></Col>
              </Row>
            </Grid>

            <FlexboxGrid>
              <FlexboxGrid.Item colspan={24}>
                <Button loading={loading} appearance="primary" onClick={handleSubmit}>
                  Guardar
                </Button>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Form>
        </Col>
      </Row>
    </Grid>
  );
}

UserForm.propTypes = {
  data: PropTypes.object,
};
